import {TrackingAPI} from 'api/tracking';
import {Button} from 'components/system/button';
import {FilterDropdown} from 'components/system/filter_dropdown';
import {FilterTwoLevelDropdown} from 'components/system/filter_two_level_dropdown';
import {TextInput} from 'components/system/text_input';
import {useDebounce} from 'hooks/debounce';
import {useIsMobile} from 'hooks/is_mobile';
import {CloseIcon, SearchIcon} from 'icons';
import {useEffect, useMemo, useState} from 'react';
import {difficultyLevelOptions} from 'types/levels';
import {useUpsell} from 'users';
import {extraFilterOptions} from 'utils/question_list';
import styles from './toolbar.module.css';

export const Toolbar = ({
  questions,
  categories,
  hasFilters,
  clearFilters,
  config,
  onConfigChange,
}: Props) => {
  const {permissionRequired} = useUpsell();
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);
  const debouncedSearchQuery = useDebounce(config.searchQuery, 2000);

  useEffect(() => {
    setShowFilters(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (debouncedSearchQuery) {
      TrackingAPI.questionSearch(config);
    }
  }, [debouncedSearchQuery]);

  const positionsOptions = useMemo(() => {
    return [...new Set(questions.flatMap(({positions}) => positions.map(({position}) => position)))]
      .map((position) => ({
        value: position,
        label: position,
      }))
      .sort((o1, o2) => o1.label.localeCompare(o2.label));
  }, [questions]);

  const categoriesOptions = useMemo(() => {
    return categories.map((category) => {
      return {
        value: category.name,
        label: category.name,
        options: category.question_tags.map((tag) => ({
          label: tag.name,
          value: tag.name,
        })),
      };
    });
  }, [categories]);

  const companyOptions = useMemo(() => {
    const companiesCount = new Map<string, number>();
    questions.forEach(({companies}) => {
      companies.forEach(({company}) => {
        companiesCount.set(company, (companiesCount.get(company) || 0) + 1);
      });
    });
    const companiesOptions = [...companiesCount.entries()]
      .sort((o1, o2) => o2[1] - o1[1])
      .map(([company, count]) => ({
        label: `${company} (${count})`,
        value: company,
      }));
    return companiesOptions;
  }, [questions]);

  const handleChange = <T extends keyof QuestionListConfig>(key: T) => {
    return (value: QuestionListConfig[T]) => {
      onConfigChange({
        ...config,
        page: 0,
        [key]: value,
      });
    };
  };

  return (
    <div className={styles.toolbar}>
      <TextInput
        name='searchQuery'
        size='sm'
        placeholder='find a question...'
        icon={<SearchIcon color='greyMedium' />}
        value={config.searchQuery}
        onChange={handleChange('searchQuery')}
      />
      {isMobile && (
        <Button size='sm' onClick={() => setShowFilters(true)}>
          Filters
        </Button>
      )}
      {showFilters && (
        <div className={styles.filters}>
          {isMobile && (
            <h3>
              Filters{' '}
              <Button
                type='text'
                color='greyMedium'
                icon={<CloseIcon color='greyMedium' />}
                onClick={() => setShowFilters(false)}>
                Close
              </Button>
            </h3>
          )}
          <FilterTwoLevelDropdown
            placeholder='Categories'
            topLevelOptions={categoriesOptions}
            value={config.tags}
            onChange={handleChange('tags')}
          />
          <FilterDropdown
            placeholder='Positions'
            options={positionsOptions}
            value={config.positions}
            onChange={handleChange('positions')}
            enableSearch={true}
            searchPlaceholder='Filter positions...'
          />
          <FilterDropdown
            placeholder='Difficulty'
            options={difficultyLevelOptions}
            value={config.levels}
            onChange={handleChange('levels')}
          />
          <FilterDropdown
            placeholder='Companies'
            options={companyOptions}
            value={config.companies}
            enableSearch={true}
            searchPlaceholder='Filter companies...'
            onChange={handleChange('companies')}
            allowOpen={permissionRequired('question_company_filter', '', {
              cta: 'question.viewCompanies',
            })}
          />
          <FilterDropdown
            placeholder='More filters'
            options={extraFilterOptions}
            value={config.extraFilters}
            onChange={handleChange('extraFilters')}
          />
          {hasFilters && (
            <Button color='orangeLight' size='sm' onClick={clearFilters}>
              clear
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

type Props = {
  questions: Question[];
  categories: QuestionCategory[];
  hasFilters: boolean;
  clearFilters: () => void;
  config: QuestionListConfig;
  onConfigChange: (config: QuestionListConfig) => void;
};
